import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Truck, Package, AlertCircle } from "lucide-react";
import { PlanGate } from "@/components/enterprise-gate";
import { useBranch } from "@/context/branch";
import { readJsonArray } from "@/lib/http";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function SupplierReportsContent() {
  const { activeBranchId } = useBranch();
  const { data, isLoading } = useQuery({
    queryKey: ["people-supplier-reports", activeBranchId],
    queryFn: async () => {
      const query = activeBranchId ? `?branchId=${activeBranchId}` : "";
      const res = await fetch(`${BASE}/api/people/supplier-reports${query}`, { credentials: "include" });
      return readJsonArray<any>(res, "supplier reports");
    },
  });

  if (isLoading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const sorted = [...(data ?? [])].sort((a, b) => b.totalSpend - a.totalSpend);
  const totalSpend = sorted.reduce((s, p) => s + p.totalSpend, 0);
  const totalOrders = sorted.reduce((s, p) => s + p.totalOrders, 0);
  const totalPending = sorted.reduce((s, p) => s + p.pendingOrders, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Suppliers</span>
              <Truck className="w-4 h-4 text-blue-500" />
            </div>
            <div className="text-xl font-bold">{sorted.length}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Spend</span>
              <Package className="w-4 h-4 text-purple-500" />
            </div>
            <div className="text-xl font-bold">{fmt(totalSpend)}</div>
            <div className="text-xs text-muted-foreground">{totalOrders} orders</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Pending Orders</span>
              <AlertCircle className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-xl font-bold">{totalPending}</div>
          </CardContent>
        </Card>
      </div>

      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-[40px]">#</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-right">Total Orders</TableHead>
              <TableHead className="text-right">Total Spend</TableHead>
              <TableHead className="text-right">Pending Orders</TableHead>
              <TableHead className="text-right">Pending Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!sorted.length ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No supplier data yet</TableCell></TableRow>
            ) : sorted.map((s, i) => (
              <TableRow key={s.id}>
                <TableCell className="text-muted-foreground text-sm">{i + 1}</TableCell>
                <TableCell>
                  <div className="font-medium">{s.name}</div>
                  {s.email && <div className="text-xs text-muted-foreground">{s.email}</div>}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{s.contactName ?? s.phone ?? "—"}</TableCell>
                <TableCell className="text-right font-semibold">{s.totalOrders}</TableCell>
                <TableCell className="text-right font-semibold">{fmt(s.totalSpend)}</TableCell>
                <TableCell className="text-right">
                  {s.pendingOrders > 0
                    ? <Badge variant="outline" className="text-amber-700 border-amber-300">{s.pendingOrders}</Badge>
                    : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right">{s.pendingValue > 0 ? fmt(s.pendingValue) : <span className="text-muted-foreground">—</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
}

export default function PeopleSupplierReports() {
  const { activeBranch } = useBranch();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Supplier Performance — {activeBranch?.name ?? "All Branches"}</h1>
        <p className="text-muted-foreground mt-1">Purchase history and spend analysis for the selected location.</p>
      </div>
      <PlanGate allowedPlans={["pro", "enterprise"]} feature="Supplier Performance Reports">
        <SupplierReportsContent />
      </PlanGate>
    </div>
  );
}
